/**
 * m3 echo Frontend - Job Log Tab
 *
 * Displays completed and failed jobs with their costs.
 * Replaces the previous Celery Worker Logs tab.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { jobLogApi, JobLogEntry } from '../../services/admin';

/**
 * Format timestamp for log entry (converts UTC to local time)
 */
const formatTimestamp = (dateStr: string | null): string => {
  if (!dateStr) return '';
  // Ensure the date is parsed as UTC if no timezone is specified
  let date: Date;
  if (dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('-', 10)) {
    // Already has timezone info
    date = new Date(dateStr);
  } else {
    // No timezone - assume UTC and append Z
    date = new Date(dateStr + 'Z');
  }
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `[${day}.${month}.${year} ${hours}:${minutes}:${seconds}]`;
};

/**
 * Format a job log entry as a log line (German)
 */
const formatLogEntry = (entry: JobLogEntry): string => {
  const timestamp = formatTimestamp(entry.executed_at);
  const status = entry.status === 'completed' ? 'abgeschlossen' : 'fehlgeschlagen';

  if (entry.entry_type === 'optimization') {
    const score = entry.overall_score !== null ? `, Score: ${entry.overall_score}` : '';
    const cost = entry.cost > 0 ? ` ${entry.cost.toFixed(4)}$` : '';
    return `${timestamp} Optimierung #${entry.optimization_test_id} ${status} (${entry.tenant_name}${score})${cost}`;
  }

  const score = entry.average_score !== null ? `, LLM Score: ${entry.average_score.toFixed(1)}` : '';
  const promptText = entry.prompt_count === 1 ? 'Prompt' : 'Prompts';
  const modelText = entry.model_count === 1 ? 'Modell' : 'Modelle';
  return `${timestamp} Job #${entry.job_id} ${status} (${entry.tenant_name}, ${entry.prompt_count} ${promptText}, ${entry.model_count} ${modelText}${score}) ${entry.cost.toFixed(4)}$`;
};

export const LogsTab: React.FC = () => {
  const [entries, setEntries] = useState<JobLogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logLimit, setLogLimit] = useState<number>(100);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState<number>(5000);

  useEffect(() => {
    loadLogs();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      loadLogs();
      const interval = setInterval(loadLogs, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, logLimit]);

  const loadLogs = async () => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const response = await jobLogApi.getJobLogs(logLimit);
      setEntries(response.data.entries || []);
    } catch (error: any) {
      console.error('Fehler beim Laden der Logs:', error);
      setLogsError(error.response?.data?.detail || 'Fehler beim Laden der Job-Logs');
    } finally {
      setLogsLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Job Log</Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadLogs}
          disabled={logsLoading}
        >
          Aktualisieren
        </Button>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 3, alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography gutterBottom>
            Anzahl Einträge: {logLimit}
          </Typography>
          <Slider
            value={logLimit}
            onChange={(_, newValue) => setLogLimit(newValue as number)}
            min={10}
            max={1000}
            step={10}
            marks={[
              { value: 10, label: '10' },
              { value: 100, label: '100' },
              { value: 500, label: '500' },
              { value: 1000, label: '1000' },
            ]}
          />
        </Box>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Auto-Refresh</InputLabel>
          <Select
            value={autoRefresh ? refreshInterval : 0}
            label="Auto-Refresh"
            onChange={(e) => {
              const value = e.target.value as number;
              if (value === 0) {
                setAutoRefresh(false);
              } else {
                setAutoRefresh(true);
                setRefreshInterval(value);
              }
            }}
          >
            <MenuItem value={0}>Aus</MenuItem>
            <MenuItem value={3000}>3 Sekunden</MenuItem>
            <MenuItem value={5000}>5 Sekunden</MenuItem>
            <MenuItem value={10000}>10 Sekunden</MenuItem>
            <MenuItem value={30000}>30 Sekunden</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {logsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {logsError}
        </Alert>
      )}

      {logsLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}

      {!logsLoading && entries.length > 0 && (
        <Paper
          sx={{
            p: 2,
            bgcolor: '#ffffff',
            fontFamily: 'monospace',
            fontSize: '13px',
            maxHeight: '600px',
            overflow: 'auto',
            border: '1px solid #e0e0e0',
          }}
        >
          {entries.map((entry, index) => (
            <Box
              key={entry.entry_type === 'optimization' ? `opt-${entry.optimization_test_id}` : `job-${entry.job_id}`}
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                borderBottom: index < entries.length - 1 ? '1px solid #eee' : 'none',
                py: 0.75,
                color: entry.status === 'failed' ? '#c62828' : entry.entry_type === 'optimization' ? '#1565c0' : '#2e7d32',
              }}
            >
              {formatLogEntry(entry)}
            </Box>
          ))}
        </Paper>
      )}

      {!logsLoading && entries.length === 0 && (
        <Alert severity="info">
          Keine abgeschlossenen Jobs vorhanden
        </Alert>
      )}
    </Box>
  );
};

export default LogsTab;
